import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Users, Calendar, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState({
    hero_title: 'Elite B2B Events',
    hero_subtitle: 'Outstanding Social Media',
    hero_description: 'Providing qualified B2B events and comprehensive social media packages to quantum, advanced cyber security, and AI security companies. One platform. Unlimited potential.',
    hero_image_url: 'https://images.unsplash.com/photo-1517982904537-aa60aa9789b0',
    section_title: 'What We Deliver',
    feature_1_title: 'Qualified Events',
    feature_1_description: 'Curated B2B networking events for quantum and cyber security leaders',
    feature_1_icon: 'shield',
    feature_2_title: 'Social Media',
    feature_2_description: 'Comprehensive social media management and content creation packages',
    feature_2_icon: 'users',
    feature_3_title: 'Event Management',
    feature_3_description: 'End-to-end event coordination and media collection',
    feature_3_icon: 'calendar',
    feature_4_title: 'Growth Tracking',
    feature_4_description: 'Revenue and engagement analytics dashboard',
    feature_4_icon: 'trending-up',
    cta_title: 'Ready to Elevate Your Security Brand?',
    cta_description: 'Join the elite network of quantum and cybersecurity companies leveraging our platform for growth.'
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/landing-content`);
      if (response.ok) {
        setContent(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch landing content:', error);
    }
  };

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGetStarted = () => {
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Play, sans-serif' }}>
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-black to-[#111]" />
        
        <div className="relative z-10">
          <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Play, sans-serif' }}>
                Quantum Security & Defence
              </h1>
              <button
                onClick={handleLogin}
                data-testid="login-button"
                className="bg-[#A2FF00] text-black px-6 py-2 font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-all duration-150"
              >
                Login
              </button>
            </div>
          </header>

          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Play, sans-serif' }}>
                  {content.hero_title}
                  <span className="block text-[#A2FF00]">{content.hero_subtitle}</span>
                </h2>
                <p className="text-lg text-gray-300 mb-8" style={{ fontFamily: 'Play, sans-serif' }}>
                  {content.hero_description}
                </p>
                <button
                  onClick={handleGetStarted}
                  data-testid="get-started-button"
                  className="bg-[#A2FF00] text-black px-8 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-all duration-150 flex items-center gap-2"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <img 
                  src={content.hero_image_url?.startsWith('/') ? `${BACKEND_URL}${content.hero_image_url}` : content.hero_image_url} 
                  alt="Corporate networking"
                  className="rounded-sm border border-[#A2FF00]/20"
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <section className="bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-16" style={{ fontFamily: 'Play, sans-serif' }}>
            {content.section_title.split(' ').map((word, i) => 
              i === content.section_title.split(' ').length - 1 ? 
                <span key={i} className="text-[#A2FF00]">{word}</span> : 
                <span key={i}>{word} </span>
            )}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-[#111] border border-white/10 rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors duration-300">
              {content.feature_1_icon === 'shield' && <Shield className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_1_icon === 'users' && <Users className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_1_icon === 'calendar' && <Calendar className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_1_icon === 'trending-up' && <TrendingUp className="w-12 h-12 text-[#A2FF00] mb-4" />}
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Play, sans-serif' }}>{content.feature_1_title}</h4>
              <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
                {content.feature_1_description}
              </p>
            </div>
            <div className="bg-[#111] border border-white/10 rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors duration-300">
              {content.feature_2_icon === 'shield' && <Shield className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_2_icon === 'users' && <Users className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_2_icon === 'calendar' && <Calendar className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_2_icon === 'trending-up' && <TrendingUp className="w-12 h-12 text-[#A2FF00] mb-4" />}
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Play, sans-serif' }}>{content.feature_2_title}</h4>
              <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
                {content.feature_2_description}
              </p>
            </div>
            <div className="bg-[#111] border border-white/10 rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors duration-300">
              {content.feature_3_icon === 'shield' && <Shield className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_3_icon === 'users' && <Users className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_3_icon === 'calendar' && <Calendar className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_3_icon === 'trending-up' && <TrendingUp className="w-12 h-12 text-[#A2FF00] mb-4" />}
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Play, sans-serif' }}>{content.feature_3_title}</h4>
              <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
                {content.feature_3_description}
              </p>
            </div>
            <div className="bg-[#111] border border-white/10 rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors duration-300">
              {content.feature_4_icon === 'shield' && <Shield className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_4_icon === 'users' && <Users className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_4_icon === 'calendar' && <Calendar className="w-12 h-12 text-[#A2FF00] mb-4" />}
              {content.feature_4_icon === 'trending-up' && <TrendingUp className="w-12 h-12 text-[#A2FF00] mb-4" />}
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Play, sans-serif' }}>{content.feature_4_title}</h4>
              <p className="text-gray-400" style={{ fontFamily: 'Play, sans-serif' }}>
                {content.feature_4_description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/1181330/pexels-photo-1181330.jpeg)' }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Play, sans-serif' }}>
            {content.cta_title.split(' ').slice(0, -4).join(' ')}{' '}
            <span className="text-[#A2FF00]">{content.cta_title.split(' ').slice(-4, -3)[0]}</span>{' '}
            {content.cta_title.split(' ').slice(-3).join(' ')}
          </h3>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" style={{ fontFamily: 'Play, sans-serif' }}>
            {content.cta_description}
          </p>
          <button
            onClick={handleLogin}
            data-testid="cta-button"
            className="bg-[#A2FF00] text-black px-10 py-5 text-lg font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-all duration-150"
          >
            Access Platform
          </button>
        </div>
      </section>

      <footer className="bg-[#0A0A0A] border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500">
          <p style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            &copy; 2025 Quantum Security & Defence. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
